import api from './client'

export interface Activity {
  id: string
  refType: 'lead' | 'deal'
  refId: string
  type: string
  content: string | null
  performedBy: string | null
  performedByUser?: { id: string; firstName: string; lastName: string }
  metadata: Record<string, unknown>
  createdAt: string
}

export interface Note {
  id: string
  refType: string
  refId: string
  content: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface Task {
  id: string
  refType: string | null
  refId: string | null
  type: 'meeting' | 'reminder'
  title: string
  description: string | null
  status: string
  dueDate: string | null
  isAllDay: boolean
  startAt: string | null
  endAt: string | null
  assignedTo: string | null
  assignedUser?: { id: string; firstName: string; lastName: string }
  completedAt: string | null
  createdAt: string
}

export interface Comment {
  id: string
  refType: string
  refId: string
  content: string
  mentions: string[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

const refPath = (companyId: string, refType: string, refId: string) =>
  `/companies/${companyId}/${refType}/${refId}`

export interface ActivityPage {
  data: Activity[]
  meta: { page: number; limit: number; total: number; totalPages: number }
}

export const activitiesApi = {
  list: (companyId: string, refType: string, refId: string, params?: { page?: number; limit?: number; offset?: number }) =>
    api.get<ActivityPage>(`${refPath(companyId, refType, refId)}/activities`, { params }).then(r => r.data),
}

export const notesApi = {
  list: (companyId: string, refType: string, refId: string) =>
    api.get<Note[]>(`${refPath(companyId, refType, refId)}/notes`),
  create: (companyId: string, refType: string, refId: string, dto: { content: string }) =>
    api.post<Note>(`${refPath(companyId, refType, refId)}/notes`, dto),
  update: (companyId: string, refType: string, refId: string, noteId: string, dto: { content: string }) =>
    api.patch<Note>(`${refPath(companyId, refType, refId)}/notes/${noteId}`, dto),
  remove: (companyId: string, refType: string, refId: string, noteId: string) =>
    api.delete(`${refPath(companyId, refType, refId)}/notes/${noteId}`),
}

export const tasksApi = {
  list: (companyId: string, refType: string, refId: string) =>
    api.get<Task[]>(`${refPath(companyId, refType, refId)}/tasks`),
  create: (companyId: string, refType: string, refId: string, dto: Partial<Task>) =>
    api.post<Task>(`${refPath(companyId, refType, refId)}/tasks`, dto),
  update: (companyId: string, refType: string, refId: string, taskId: string, dto: Partial<Task>) =>
    api.patch<Task>(`${refPath(companyId, refType, refId)}/tasks/${taskId}`, dto),
  remove: (companyId: string, refType: string, refId: string, taskId: string) =>
    api.delete(`${refPath(companyId, refType, refId)}/tasks/${taskId}`),
}

export const commentsApi = {
  list: (companyId: string, refType: string, refId: string) =>
    api.get<Comment[]>(`${refPath(companyId, refType, refId)}/comments`),
  create: (companyId: string, refType: string, refId: string, dto: { content: string }) =>
    api.post<Comment>(`${refPath(companyId, refType, refId)}/comments`, dto),
  remove: (companyId: string, refType: string, refId: string, commentId: string) =>
    api.delete(`${refPath(companyId, refType, refId)}/comments/${commentId}`),
}

export const emailsApi = {
  send: (companyId: string, refType: string, refId: string, dto: Record<string, unknown>) =>
    api.post(`${refPath(companyId, refType, refId)}/emails/send`, dto),
  list: (companyId: string, refType: string, refId: string, page = 1, limit = 50) =>
    api.get(`${refPath(companyId, refType, refId)}/emails`, { params: { page, limit } }),
}
